import { Box, Typography } from '@mui/material';
import React from 'react';
import LegalDocumentLayout, { headingSx, listSx, paragraphSx } from '../../components/front/LegalDocumentLayout';

const LAST_UPDATED = '16 de junho de 2026';

function PrivacyPolicyPage() {
    return (
        <LegalDocumentLayout title="Política de Privacidade" lastUpdated={LAST_UPDATED}>
            <Typography sx={paragraphSx}>
                Esta Política de Privacidade descreve como a <strong>Andrebian Soluções Web</strong> (CNPJ 23.558.573/0001-30),
                controladora do <strong>Acompanha 3D</strong>, trata dados pessoais em conformidade com a Lei Geral de
                Proteção de Dados Pessoais (Lei nº 13.709/2018 — LGPD).
            </Typography>

            <Typography component="h2" sx={headingSx}>1. Quem somos</Typography>
            <Typography sx={paragraphSx}>
                <strong>Controlador:</strong> Andrebian Soluções Web<br />
                <strong>CNPJ:</strong> 23.558.573/0001-30<br />
                <strong>Localização:</strong> Curitiba — Paraná — Brasil<br />
                <strong>Contato para privacidade:</strong>{' '}
                <a href="mailto:privacidade@acompanha3d.com.br">privacidade@acompanha3d.com.br</a>
            </Typography>

            <Typography component="h2" sx={headingSx}>2. Dados pessoais que coletamos</Typography>
            <Typography sx={paragraphSx}>Podemos tratar as seguintes categorias de dados:</Typography>
            <Box component="ul" sx={listSx}>
                <li><strong>Cadastro:</strong> nome, e-mail, senha (armazenada de forma criptografada), nome da loja.</li>
                <li><strong>Perfil:</strong> foto, WhatsApp, site, redes sociais e demais campos opcionais informados por você.</li>
                <li><strong>Operacionais:</strong> dados de clientes, pedidos, produtos, impressoras e transações inseridos na plataforma.</li>
                <li><strong>Técnicos:</strong> endereço IP, data e hora de acesso, logs de uso e identificadores de sessão.</li>
                <li><strong>Comunicação:</strong> registros de e-mails transacionais (ativação de conta, recuperação de senha).</li>
            </Box>

            <Typography component="h2" sx={headingSx}>3. Finalidades e bases legais (Art. 7º da LGPD)</Typography>
            <Typography sx={paragraphSx}>Tratamos dados pessoais para as finalidades abaixo:</Typography>
            <Box component="ul" sx={listSx}>
                <li>
                    <strong>Prestação do serviço</strong> — execução de contrato ou procedimentos preliminares (cadastro,
                    login, gestão da conta e funcionalidades da plataforma).
                </li>
                <li>
                    <strong>Segurança</strong> — prevenção a fraudes, proteção da conta e integridade do sistema (interesse legítimo).
                </li>
                <li>
                    <strong>Comunicações essenciais</strong> — envio de e-mails de ativação, redefinição de senha e avisos
                    operacionais necessários ao serviço (execução de contrato).
                </li>
                <li>
                    <strong>Cumprimento legal</strong> — atendimento a obrigações legais ou regulatórias, quando aplicável.
                </li>
                <li>
                    <strong>Melhoria do produto</strong> — análise agregada de uso para aprimorar funcionalidades (interesse legítimo,
                    com minimização de dados).
                </li>
            </Box>

            <Typography component="h2" sx={headingSx}>4. Dados de terceiros inseridos por você</Typography>
            <Typography sx={paragraphSx}>
                Ao cadastrar clientes ou outras pessoas na plataforma, você atua como controlador independente em relação a
                esses dados e declara possuir base legal para coletá-los e tratá-los. O Acompanha 3D atua como operador
                nesse contexto, processando os dados conforme suas instruções para prestar o serviço contratado.
            </Typography>

            <Typography component="h2" sx={headingSx}>5. Compartilhamento de dados</Typography>
            <Typography sx={paragraphSx}>
                Não vendemos dados pessoais. Podemos compartilhar informações apenas nas situações abaixo:
            </Typography>
            <Box component="ul" sx={listSx}>
                <li>
                    <strong>Prestadores de serviço</strong> — hospedagem, infraestrutura de e-mail (ex.: Resend) e provedores
                    de nuvem necessários à operação, sob contratos que exigem proteção adequada dos dados.
                </li>
                <li>
                    <strong>Obrigação legal</strong> — quando exigido por autoridade competente ou decisão judicial.
                </li>
                <li>
                    <strong>Proteção de direitos</strong> — para investigar violações desta Política, dos Termos de Uso ou da lei.
                </li>
            </Box>

            <Typography component="h2" sx={headingSx}>6. Transferência internacional</Typography>
            <Typography sx={paragraphSx}>
                Alguns fornecedores de tecnologia podem processar dados em servidores localizados fora do Brasil. Nesses casos,
                adotamos medidas compatíveis com a LGPD, como cláusulas contratuais e avaliação de garantias de proteção
                oferecidas pelo prestador.
            </Typography>

            <Typography component="h2" sx={headingSx}>7. Armazenamento e segurança</Typography>
            <Typography sx={paragraphSx}>
                Adotamos medidas técnicas e administrativas para proteger dados pessoais contra acessos não autorizados,
                perda, alteração ou divulgação indevida, incluindo criptografia de senhas, comunicação via HTTPS e controle
                de acesso à infraestrutura. Nenhum sistema é totalmente imune a riscos; recomendamos o uso de senhas fortes
                e únicas.
            </Typography>

            <Typography component="h2" sx={headingSx}>8. Retenção de dados</Typography>
            <Typography sx={paragraphSx}>
                Mantemos dados pessoais pelo tempo necessário para cumprir as finalidades descritas nesta Política, respeitar
                obrigações legais e resolver disputas. Após o encerramento da conta, dados podem ser eliminados ou anonimizados
                em prazo razoável, salvo retenção exigida por lei ou para exercício regular de direitos.
            </Typography>

            <Typography component="h2" sx={headingSx}>9. Seus direitos como titular (Art. 18 da LGPD)</Typography>
            <Typography sx={paragraphSx}>Você pode solicitar, mediante requisição ao controlador:</Typography>
            <Box component="ul" sx={listSx}>
                <li>Confirmação da existência de tratamento e acesso aos dados.</li>
                <li>Correção de dados incompletos, inexatos ou desatualizados.</li>
                <li>Anonimização, bloqueio ou eliminação de dados desnecessários ou tratados em desconformidade.</li>
                <li>Portabilidade dos dados a outro fornecedor, quando aplicável.</li>
                <li>Eliminação dos dados tratados com consentimento, quando cabível.</li>
                <li>Informação sobre compartilhamentos e sobre a possibilidade de não consentir.</li>
                <li>Revogação do consentimento, quando o tratamento tiver essa base legal.</li>
            </Box>
            <Typography sx={paragraphSx}>
                Para exercer seus direitos, envie e-mail para{' '}
                <a href="mailto:privacidade@acompanha3d.com.br">privacidade@acompanha3d.com.br</a>.
                Responderemos em prazo razoável, conforme a LGPD.
            </Typography>

            <Typography component="h2" sx={headingSx}>10. Cookies e armazenamento local</Typography>
            <Typography sx={paragraphSx}>
                Utilizamos cookies e armazenamento local do navegador (ex.: token de autenticação) para manter sua sessão
                logada e preferências básicas (como tema claro/escuro). Não utilizamos cookies de publicidade de terceiros
                na área autenticada do serviço.
            </Typography>

            <Typography component="h2" sx={headingSx}>11. Crianças e adolescentes</Typography>
            <Typography sx={paragraphSx}>
                O Acompanha 3D é destinado a maiores de 18 anos ou a menores devidamente representados por responsáveis legais.
                Não coletamos intencionalmente dados de crianças sem o consentimento adequado.
            </Typography>

            <Typography component="h2" sx={headingSx}>12. Alterações desta Política</Typography>
            <Typography sx={paragraphSx}>
                Esta Política pode ser atualizada para refletir mudanças legais ou operacionais. A data da última revisão
                será indicada no topo da página. Alterações relevantes poderão ser comunicadas por e-mail ou aviso na plataforma.
            </Typography>

            <Typography component="h2" sx={headingSx}>13. Encarregado de dados (DPO)</Typography>
            <Typography sx={paragraphSx}>
                Para questões relacionadas à proteção de dados pessoais, entre em contato com nosso canal de privacidade:{' '}
                <a href="mailto:privacidade@acompanha3d.com.br">privacidade@acompanha3d.com.br</a>.
            </Typography>
        </LegalDocumentLayout>
    );
}

export default PrivacyPolicyPage;
